import { buildUrl } from '@agnivo/api-client';
import { ENTITLEMENT_SURFACE_EVENT_NAME, FUNNEL_STEP_EVENT_NAME, isEntitlementSurfaceState, isFunnelStepToken, isTguidMissingStep, MINIAPP_TGUID_MISSING_EVENT_NAME, ROUTE_VIEW_EVENT_NAME, admitsUmamiEventData, } from './clientTelemetryContract';
import { mintCorrelationToken } from './correlationToken';
import { sendUmamiPayload } from './umamiSendWindow';
/**
 * ADR-141 D1.1 enforcement point 1 — THIS MODULE is the only exit to the Umami
 * plane; its exported senders are its only exits.
 *
 * ⚠️ This sentence used to enumerate the senders by name and count ("its two
 * exported senders … are its only exits"), and a third sender
 * (`createMiniappTguidMissingSender`) made that false the same way "the ONLY
 * sender" went false when the second one landed (architect R5, 2026-09-02) —
 * same defect, recreated by the fix that corrected it (architect R-E,
 * 2026-09-14). The conclusion below survives any COUNT of senders; only an
 * enumeration by name or number can go stale, so this sentence carries neither.
 * Same defect class caught three times on `tenant-service!631` the same day.
 *
 * WHAT MAKES THIS THE ONLY PLACE. The vendor populates most of its own columns
 * from the request, so this module is the single point in the path where "the
 * collector never receives it" is a true statement rather than a hope. Anything
 * that sends to Umami from elsewhere silently reintroduces the fields this file
 * exists to withhold.
 *
 * ⛔ "ELSEWHERE" MEANS ANOTHER MODULE, NOT ANOTHER FUNCTION HERE. A second sender
 * in this file shares `buildUrl`, the empty `title`/`referrer`, the token-as-`url`
 * discipline, the never-retry rule and `allowlistedData` — so the property belongs
 * to the transport, and the transport did not move. A caller gets a CALL
 * (`reportFunnelStep(token)`), never a transport. Adding a third sender is fine;
 * adding a `fetch` to the collector anywhere else is not.
 *
 * ⓘ The FILENAME is now narrower than the module. Renaming it would touch
 * `index.ts` and every importer for zero behaviour change, so it is deliberately
 * left — noted here rather than left for the next reader to wonder about.
 *
 * ⛔ `url` IS THE ROUTE TOKEN, never the real path or query. `title` and
 * `referrer` are sent EMPTY and are sent explicitly rather than omitted: Umami's
 * client library fills them from `document` when absent, so omission is not
 * absence — it is a default that reads the page. An empty string is a decision;
 * a missing key is whatever the vendor decides.
 *
 * ⛔ NEVER retries. Umami's send is fire-and-forget with no idempotency key, so a
 * retry manufactures the second row that breaks ADR-141 D2's 1+1 cardinality
 * check — and a broken join is worse than a missing one, because it looks like
 * data. A failed send is a dropped observation, on purpose.
 *
 * ⚠️ "DROPPED" IS NOT THE SAME AS "UNOBSERVABLE", and the difference decides
 * whether a failure counter can exist at all (measured 2026-09-18). Every sender
 * here discards its response — see the resolve-path warning in
 * `createRouteViewSender`. Whether that response *could* be read is **not** a
 * platform guarantee: cross-origin, a 4xx resolves to a readable `response.ok`
 * **only while the collector returns `access-control-allow-origin` on ERROR
 * responses**. Measured live: `/api/send` does, on a 405 error as well as on a 204
 * preflight — so this is a property of the collector's config, one change away
 * from vanishing. Withhold it and a 400 arrives as `TypeError: Failed to fetch`:
 * unreadable, and **indistinguishable from a dead network**.
 * ⇒ Any instrument built here to tell "the collector rejected it" from "I could
 * not observe it" is reading a setting **it does not own**, so it must be able to
 * report **`unobservable`** as a third state rather than a healthy-looking zero.
 *
 * ⛔ No `umami.identify()`, here or anywhere: its sink is a durable
 * session-scoped `distinct_id`, so the CORRECT use of it produces exactly the
 * artifact ADR-141 prohibits. A lint asserts the call's absence repo-wide.
 *
 * ⭐ BOUNDED BY REDUNDANCY, NOT BY RECENCY (architect ruling 2026-09-02,
 * `decisions/architect/2026-09-02-adr141-d11-route-view-bound-by-redundancy-not-by-recency.md`).
 * A token bucket drops the TAIL of a session, which on a funnel metric is a
 * silent undercount: a truncated session is indistinguishable from a short one.
 * So this drops only observations that carry no new information, and a normal
 * session loses nothing.
 */
/** Same-token suppression window. A repeat of the last token inside this is redundant. */
export const SAME_TOKEN_WINDOW_MS = 2_000;
/**
 * Coarse absolute ceiling — a CIRCUIT BREAKER, not a budget.
 *
 * Sized to separate humans from loops, not to ration humans: nothing a person
 * does reaches 60 route views in a minute, while an A→B→A→B router loop does —
 * and such a loop alternates tokens, so same-token suppression cannot see it.
 */
export const ROUTE_VIEW_CEILING_PER_MINUTE = 60;
export const CEILING_WINDOW_MS = 60_000;
function defaultElapsedClock() {
    const p = globalThis.performance;
    if (p && typeof p.now === 'function')
        return () => p.now();
    return Date.now;
}
/**
 * Builds the `data` object from the CONTRACT's allowlist rather than a literal.
 *
 * ⛔ Reads the declared keys and copies only those. A hand-written object here
 * would drift from the contract silently — the allowlist would still exist, and
 * would still be published in the artifact, while the sender ignored it.
 *
 * Filters on KEY and, where the contract declares a value domain, on VALUE.
 *
 * ⚠️ EXPORTED ONLY SO IT CAN BE TESTED, and that is worth stating plainly:
 * `createRouteViewSender` passes exactly the two keys the contract declares, so
 * through the public API this filter is a NO-OP today and mutating it away leaves
 * the sender's whole suite green (measured, 2026-09-02). Its value is entirely
 * prospective — it is what makes a future call site that adds an undeclared key
 * fail closed instead of leaking it. An untested guard is indistinguishable from
 * an absent one, so it is tested directly rather than through a caller that
 * cannot reach its interesting branch.
 *
 * ⛔ THE VALUE ARM IS UNREACHABLE THROUGH THE FUNNEL SENDER TOO, for a different
 * reason worth stating rather than discovering: `createFunnelStepSender` rejects an
 * undeclared token with `isFunnelStepToken` and returns early, so no bad value ever
 * reaches this function in production. That is two guards over one mechanism, which
 * is deliberate — the sender's check exists to produce an OBSERVABLE drop (it counts
 * and reports via `onUnknownStep`), this one exists to be structurally unbypassable
 * by a future second caller. Neither is redundant, but only the first is reachable
 * from a caller, so the second is mutation-tested DIRECTLY. Do not "simplify" by
 * deleting either: dropping the sender's check loses the signal, dropping this one
 * loses the guarantee.
 */
export function allowlistedData(values, eventName) {
    // ⛔ The event name is REQUIRED, not defaulted to `route_view`. A default would be
    // fail-CLOSED here (a forgetful caller would get route_view's keys and silently
    // drop its own), so this is not a leak risk — it is a legibility one: the
    // allowlist is per event name, and a call site that does not say which event it is
    // sending cannot be reviewed against the right list.
    const data = {};
    // `Object.entries`, not `Object.keys` + index: under `noUncheckedIndexedAccess`
    // the indexed read is `string | undefined`, and the honest fix is to carry the
    // value rather than to assert it is present.
    for (const [key, value] of Object.entries(values)) {
        // ⛔ Filters on KEY **and VALUE** since the contract gained value domains
        // (architect option 2, 2026-09-02). `admitsUmamiEventData` is one call rather
        // than a key check `&&` a domain check — see its docblock for why splitting it
        // would be fail-open when composed in the wrong order.
        if (value !== undefined && admitsUmamiEventData(eventName, key, value))
            data[key] = value;
    }
    return data;
}
/**
 * `apiMode !== 'live'` returns a no-op, mirroring `createClientEventReporter`: a
 * mock-mode shell would otherwise post real analytics from a fixture run.
 */
export function createRouteViewSender(cfg) {
    if (cfg.apiMode !== 'live')
        return () => { };
    const elapsedNow = cfg.elapsedNow ?? defaultElapsedClock();
    const url = buildUrl(cfg.collectorUrl, '/api/send');
    // A ONE-TOKEN memo, not a set: `ClientEventRoute` is a closed vocabulary and the
    // rule is "same as the LAST one", so a set would remember routes that are no
    // longer the comparison target and suppress a legitimate return visit.
    let lastRoute = null;
    let lastSentAt = 0;
    let sentTimes = [];
    let ceilingReported = false;
    // ⛔ NO monotonic high-water clamp here, deliberately — `createEventBudget` has
    // one and this does not, so the difference is worth stating rather than reading
    // as an omission.
    //
    // The budget's clock drives REFILL: tokens ACCUMULATE with elapsed time, so a
    // `performance.now()` rollback across suspend/resume genuinely misbehaves there
    // (web!513's review counterexample), and it needs the mark.
    //
    // This sender only EXPIRES. Both windows ask `t - ts < WINDOW`, and a negative
    // age always satisfies that — so a backwards clock can never age an entry OUT;
    // it can only over-suppress, briefly, until the clock climbs back. I wrote the
    // clamp first, then mutated it away and the whole suite stayed green. Rather
    // than write a test to justify the line, I checked whether it could change any
    // outcome: across rewind, rewind-then-advance, and forward-then-back, the
    // decisions are identical with and without it. An arm whose necessity cannot be
    // demonstrated is removed, not kept and covered — the same call `web!533`'s
    // review reached on the `ImportBinding` guard.
    return (route) => {
        const t = elapsedNow();
        if (route === lastRoute && t - lastSentAt < SAME_TOKEN_WINDOW_MS)
            return;
        sentTimes = sentTimes.filter((ts) => t - ts < CEILING_WINDOW_MS);
        if (sentTimes.length >= ROUTE_VIEW_CEILING_PER_MINUTE) {
            // First trip only — the signal is "a loop started", not one per dropped
            // event, and a per-event signal would itself become the flood.
            if (!ceilingReported) {
                ceilingReported = true;
                try {
                    cfg.onCeilingTripped?.();
                }
                catch {
                    /* an incident signal that can throw into the sender is not one */
                }
            }
            return;
        }
        // Recorded BEFORE the `try`, deliberately: every bound here is on
        // OBSERVATIONS, not on successful sends. Moving these inside — or after —
        // would make a failing collector uncap the loop detector at exactly the
        // moment the ceiling matters most, since nothing would ever be counted.
        // `lastRoute`/`lastSentAt` are the same-token window and `sentTimes` is the
        // per-minute ceiling; both answer "how often is this code being asked to
        // send", which is knowable here and independent of whether the network
        // agrees. (architect, web!537 review.)
        lastRoute = route;
        lastSentAt = t;
        sentTimes.push(t);
        try {
            const data = allowlistedData({
                auth_mode: cfg.authMode,
                client_shell: cfg.clientShell,
                // The ONLY correlation carrier, and `event_data` is the only place D2
                // permits it to ride. Minted per action through the one authority, so
                // D9.12's call-graph rule is satisfied by construction rather than by
                // review.
                correlation_token: mintCorrelationToken(),
            }, ROUTE_VIEW_EVENT_NAME);
            sendUmamiPayload(cfg.fetchImpl ?? fetch, url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
                body: JSON.stringify({
                    type: 'event',
                    payload: {
                        website: cfg.websiteId,
                        name: ROUTE_VIEW_EVENT_NAME,
                        url: route,
                        title: '',
                        referrer: '',
                        // ⛔ THE SHELL'S OWN REAL HOST, AS A BUILD-TIME CONSTANT — NOT a masking
                        // sentinel. Three prior shapes each shipped and each failed:
                        //
                        //   hostname: ''/undefined  falsy -> umami's `||` -> stored as 'localhost'
                        //   hostname: null          Zod REJECTS -> 400 -> NO ROW (fetch RESOLVES on
                        //                           4xx/5xx; `.catch` never sees it — two silent
                        //                           paths, not one)
                        //   hostname: 'redacted'    truthy, stored as-is, row written — but REJECTED
                        //                           by `infra`'s `umami-schema-pin-guard` (44 rows,
                        //                           `UmamiZeroClassViolationDev`, 2026-09-18): that
                        //                           control's premise is `hostname` is a BOUNDED,
                        //                           KNOWN-HOST dimension, not personal data needing a
                        //                           mask, and a sentinel that means "not a host" is
                        //                           out of set by construction.
                        //
                        // ⇒ `cfg.hostname` (see `RouteViewSenderConfig.hostname`) is this shell's
                        // deployed host — `app.agnivo.org`/`app.dev.agnivo.org` for web-ui,
                        // `miniapp.agnivo.org`/`miniapp.dev.agnivo.org` for mini-app — one of the
                        // guard manifest's `allowed:` values. A first-party surface host is not
                        // personal data, which is why naming it strictly dominates every masking
                        // shape tried before it: no 'localhost' collapse, no 400, no out-of-set
                        // rejection, and the value is honest rather than sender-derived-but-hidden.
                        //
                        // ⛔ STILL A CONSTANT — never `window.location.hostname` (a local dev run
                        // yields `localhost`, not in the allowlist) or anything else computed from
                        // the request/document/device.
                        //
                        // Architect ruling: `decisions/architect/2026-09-16-umami-hostname-fix-bprime-null-shape-ruling.md`,
                        // AMENDMENT 2026-09-18T08:50Z (supersedes every earlier shape in this file's
                        // history, including its own two prior amendments — read the file's audit
                        // trail before proposing a fifth shape).
                        hostname: cfg.hostname,
                        data,
                    },
                }),
            }, cfg.umamiWindow);
        }
        catch {
            /* fire-and-forget: a tracker that can fail the caller is not one. This also
               catches `mintCorrelationToken()` throwing `CorrelationTokenUnavailableError`
               on a non-secure context — no token means no send, never an unminted one. */
        }
    };
}
/**
 * ⛔ SEPARATE ONCE-FLAGS AND A SEPARATE CEILING FROM `createRouteViewSender`.
 * Shares this module and every field rule; shares NO counter. (Architect R4,
 * 2026-09-02 — I had proposed sharing the suppression and ceiling machinery and
 * that was wrong.)
 *
 * WHY SHARING THE CEILING IS THE DEFECT. `ROUTE_VIEW_CEILING_PER_MINUTE` exists to
 * catch an `A→B→A→B` router loop. Shared, that loop eats the budget and the funnel
 * steps drop silently — AT THE MOMENT SOMETHING IS GOING WRONG, which is when the
 * funnel is most worth having. The ruling four hours earlier rejected
 * recency-bounded dropping precisely because "a token bucket drops the TAIL of a
 * session, which on a funnel metric is a silent undercount"; sharing the ceiling
 * reintroduces that on the very metric that ruling protects.
 *
 * WHY SHARING THE 2s WINDOW IS ALSO WRONG. That window is a claim about
 * NAVIGATION — a repeat of the last route inside 2s carries no new information.
 * Three of these four tokens are first-per-session by construction, where a
 * once-flag is a stronger and more honest guard than a time window; the fourth
 * legitimately repeats. Neither semantics is the route one.
 */
/** Tokens that can occur at most once per session, by construction. */
const ONCE_PER_SESSION_STEPS = new Set([
    'first_agent_open',
    'first_message_sent',
    'first_answer_visible',
]);
/**
 * Absolute ceiling for funnel steps — deliberately far below the route-view one.
 *
 * Three of the four tokens are once-per-session, so the only repeatable token is
 * `agent_create_confirmed`. Nothing a person does creates twenty agents in a
 * minute; a loop does. Same circuit-breaker intent as the route ceiling, sized for
 * a much smaller legitimate range.
 */
export const FUNNEL_STEP_CEILING_PER_MINUTE = 20;
/**
 * ⛔ NO `correlation_token` ON THIS EVENT, and it is condition 4 of the ruling
 * rather than an omission: no D2 action token, no journey key, no cross-plane join,
 * no per-user attribution. These are single-plane counts, which is what keeps the
 * funnel clear of D5.2 entirely. Adding the token would hand it a join path into
 * the Class-1 plane.
 *
 * ⚠️ HONEST DESCRIPTION, for whoever reads a chart built on this: these are
 * "single-plane counts ON A PLANE WHERE WITHIN-PLANE SEQUENCING IS POSSIBLE", not
 * "counts that identify nobody". The tokens are ordered and per-actor-meaningful,
 * and the Umami plane carries a vendor session identifier (the IP+UA/salt
 * derivation registered on ADR-058 R29), so a per-session step sequence is
 * reconstructible from data that plane already collects. This event does not create
 * that property and does not worsen it — but the sentence belongs where someone
 * planning against the data will meet it.
 */
export function createFunnelStepSender(cfg) {
    // Same gate as the route sender and `createClientEventReporter`: a mock-mode
    // shell would otherwise post real analytics from a fixture run.
    if (cfg.apiMode !== 'live')
        return () => { };
    const elapsedNow = cfg.elapsedNow ?? defaultElapsedClock();
    const url = buildUrl(cfg.collectorUrl, '/api/send');
    const sentOnce = new Set();
    let sentTimes = [];
    let ceilingReported = false;
    let unknownReported = false;
    return (step) => {
        // Runtime check, not just the compile-time union: this is reachable from an
        // untyped caller, and from a value widened to `string` crossing a context
        // boundary. An undeclared token is dropped BEFORE any send.
        if (!isFunnelStepToken(step)) {
            if (!unknownReported) {
                unknownReported = true;
                try {
                    cfg.onUnknownStep?.(step);
                }
                catch {
                    /* a drop signal that can throw into the sender is not one */
                }
            }
            return;
        }
        if (ONCE_PER_SESSION_STEPS.has(step) && sentOnce.has(step))
            return;
        const t = elapsedNow();
        sentTimes = sentTimes.filter((ts) => t - ts < CEILING_WINDOW_MS);
        if (sentTimes.length >= FUNNEL_STEP_CEILING_PER_MINUTE) {
            if (!ceilingReported) {
                ceilingReported = true;
                try {
                    cfg.onCeilingTripped?.();
                }
                catch {
                    /* as above */
                }
            }
            return;
        }
        // Before the `try`, for the reason given at the route-view site above: the
        // bound is on OBSERVATIONS, not successful sends. `sentOnce` additionally
        // makes a once-per-session step once-per-ATTEMPT — a failed send is not a
        // licence to re-send, because the never-retry rule is what keeps D2's 1+1
        // cardinality true.
        sentOnce.add(step);
        sentTimes.push(t);
        try {
            const data = allowlistedData({ client_shell: cfg.clientShell, funnel_step: step }, FUNNEL_STEP_EVENT_NAME);
            sendUmamiPayload(cfg.fetchImpl ?? fetch, url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
                body: JSON.stringify({
                    type: 'event',
                    payload: {
                        website: cfg.websiteId,
                        name: FUNNEL_STEP_EVENT_NAME,
                        // ⛔ The STEP TOKEN as `url`, never a real path — same discipline as the
                        // route sender. `title` and `referrer` are sent EMPTY rather than
                        // omitted, because Umami's client fills them from `document` when
                        // absent: omission is a default that reads the page, not absence.
                        url: step,
                        title: '',
                        referrer: '',
                        // See createRouteViewSender — same real-host, build-time-constant fix.
                        hostname: cfg.hostname,
                        data,
                    },
                }),
            }, cfg.umamiWindow);
        }
        catch {
            /* fire-and-forget: a tracker that can fail the caller is not one */
        }
    };
}
/**
 * ⛔ THIRD SENDER. Named here rather than left implicit, per R-E above: this module's
 * own claim was "the only two", and adding a third without correcting that sentence
 * would recreate the exact defect the sentence itself exists to flag.
 *
 * ADR-082 mini-app tg_uid-missing reopen surface (architect ruling
 * `decisions/architect/2026-09-14-miniapp-tguid-counters-one-event-with-a-step-dimension-no-token-self-serve.md`).
 * Shares this module's transport discipline (buildUrl, empty title/referrer,
 * token-as-`url`, never-retry, `allowlistedData`) and NOTHING ELSE with the other two
 * senders — no shared ceiling/suppression state (R-D, same reasoning `createFunnelStepSender`'s
 * docblock gives for not sharing with the route sender: a loop on one must not silence
 * the other), no correlation token (R-B: nothing this event answers needs the
 * cross-plane join D2 gates), and no once-per-session/same-token suppression: BOTH
 * `error_received` and `reopen_clicked` legitimately repeat in a session (a tg_uid-missing
 * session can fail more than once across reopen cycles; a user can click «Закрыть» more
 * than once) — R-D asks for this to be an explicit choice rather than an inherited
 * default, so: no suppression window at all here, only the ceiling below as a circuit
 * breaker against a loop.
 */
export const MINIAPP_TGUID_MISSING_CEILING_PER_MINUTE = 20;
/**
 * `apiMode !== 'live'` returns a no-op, mirroring the other two senders in this file.
 */
export function createMiniappTguidMissingSender(cfg) {
    if (cfg.apiMode !== 'live')
        return () => { };
    const elapsedNow = cfg.elapsedNow ?? defaultElapsedClock();
    const url = buildUrl(cfg.collectorUrl, '/api/send');
    let sentTimes = [];
    let ceilingReported = false;
    let unknownReported = false;
    return (input) => {
        // Runtime check, not just the compile-time union — see `onUnknownStep`'s docblock.
        // An undeclared step is dropped BEFORE any send or ceiling accounting, same as
        // `createFunnelStepSender`'s guard.
        if (!isTguidMissingStep(input.step)) {
            if (!unknownReported) {
                unknownReported = true;
                try {
                    cfg.onUnknownStep?.(input.step);
                }
                catch {
                    /* a drop signal that can throw into the sender is not one */
                }
            }
            return;
        }
        const t = elapsedNow();
        // Bound is on OBSERVATIONS, not successful sends — same reasoning the other two
        // senders give: a failing collector must not uncap the loop detector exactly when
        // the ceiling matters most.
        sentTimes = sentTimes.filter((ts) => t - ts < CEILING_WINDOW_MS);
        if (sentTimes.length >= MINIAPP_TGUID_MISSING_CEILING_PER_MINUTE) {
            if (!ceilingReported) {
                ceilingReported = true;
                try {
                    cfg.onCeilingTripped?.();
                }
                catch {
                    /* an incident signal that can throw into the sender is not one */
                }
            }
            return;
        }
        sentTimes.push(t);
        try {
            // `tg_uid_present` is hardcoded `'false'`, never a parameter: this event only
            // ever fires because tg_uid IS missing, so the alternative value is not this
            // producer's to send (see the contract literal's note on why both are
            // nonetheless declared).
            const raw = input.step === 'error_received'
                ? {
                    client_shell: cfg.clientShell,
                    step: input.step,
                    copy_version: cfg.copyVersion,
                    error_info_reason: input.errorInfoReason,
                    tg_uid_present: 'false',
                }
                : {
                    client_shell: cfg.clientShell,
                    step: input.step,
                    copy_version: cfg.copyVersion,
                };
            const data = allowlistedData(raw, MINIAPP_TGUID_MISSING_EVENT_NAME);
            sendUmamiPayload(cfg.fetchImpl ?? fetch, url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
                body: JSON.stringify({
                    type: 'event',
                    payload: {
                        website: cfg.websiteId,
                        name: MINIAPP_TGUID_MISSING_EVENT_NAME,
                        url: input.step,
                        title: '',
                        referrer: '',
                        // See createRouteViewSender — same real-host, build-time-constant fix.
                        hostname: cfg.hostname,
                        data,
                    },
                }),
            }, cfg.umamiWindow);
        }
        catch {
            /* fire-and-forget: a tracker that can fail the caller is not one */
        }
    };
}
export const ENTITLEMENT_SURFACE_CEILING_PER_MINUTE = 20;
/**
 * ADR-051 S7c-7 (architect ruling
 * `decisions/architect/2026-09-18-s7c7-entitlement-surface-analytics-event-shape.md`).
 *
 * ⛔ NO `correlation_token`, same reasoning as `createFunnelStepSender`'s: this is a single-plane
 * Umami count, no Class-1 event joins to it.
 *
 * `step` is hardcoded `'shown'`, never a parameter — the contract admits exactly one value today,
 * and a caller-supplied step would let a future call site widen the payload ahead of the contract
 * entry that is supposed to gate it (same reasoning `createMiniappTguidMissingSender` gives for
 * hardcoding `tg_uid_present: 'false'`).
 *
 * `apiMode !== 'live'` returns a no-op, mirroring the other senders in this file.
 */
export function createEntitlementSurfaceSender(cfg) {
    if (cfg.apiMode !== 'live')
        return () => { };
    const elapsedNow = cfg.elapsedNow ?? defaultElapsedClock();
    const url = buildUrl(cfg.collectorUrl, '/api/send');
    let sentTimes = [];
    let ceilingReported = false;
    let unknownReported = false;
    return (state) => {
        // Runtime check, not just the compile-time union — see `onUnknownState`'s docblock. An
        // undeclared state is dropped BEFORE any send or ceiling accounting, same as
        // `createFunnelStepSender`'s guard.
        if (!isEntitlementSurfaceState(state)) {
            if (!unknownReported) {
                unknownReported = true;
                try {
                    cfg.onUnknownState?.(state);
                }
                catch {
                    /* a drop signal that can throw into the sender is not one */
                }
            }
            return;
        }
        const t = elapsedNow();
        // Bound is on OBSERVATIONS, not successful sends — same reasoning the other senders give: a
        // failing collector must not uncap the loop detector exactly when the ceiling matters most.
        sentTimes = sentTimes.filter((ts) => t - ts < CEILING_WINDOW_MS);
        if (sentTimes.length >= ENTITLEMENT_SURFACE_CEILING_PER_MINUTE) {
            if (!ceilingReported) {
                ceilingReported = true;
                try {
                    cfg.onCeilingTripped?.();
                }
                catch {
                    /* an incident signal that can throw into the sender is not one */
                }
            }
            return;
        }
        sentTimes.push(t);
        try {
            const raw = {
                client_shell: cfg.clientShell,
                state,
                step: 'shown',
            };
            const data = allowlistedData(raw, ENTITLEMENT_SURFACE_EVENT_NAME);
            sendUmamiPayload(cfg.fetchImpl ?? fetch, url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                keepalive: true,
                body: JSON.stringify({
                    type: 'event',
                    payload: {
                        website: cfg.websiteId,
                        name: ENTITLEMENT_SURFACE_EVENT_NAME,
                        url: state,
                        title: '',
                        referrer: '',
                        // See createRouteViewSender — same real-host, build-time-constant fix.
                        hostname: cfg.hostname,
                        data,
                    },
                }),
            }, cfg.umamiWindow);
        }
        catch {
            /* fire-and-forget: a tracker that can fail the caller is not one */
        }
    };
}
//# sourceMappingURL=routeViewSender.js.map